/**
 * Cookie Service - Utility functions for managing cookies
 */

/**
 * Set a cookie with the given name, value, and expiration days
 */
export const setCookie = (name: string, value: string, days = 365): void => {
  try {
    const date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    const expires = `expires=${date.toUTCString()}`;
    document.cookie = `${name}=${value};${expires};path=/;SameSite=Strict`;
  } catch (error) {
    console.error(`Error setting cookie ${name}:`, error);
  }
};

/**
 * Get a cookie value by name
 * Returns null if the cookie doesn't exist
 */
export const getCookie = (name: string): string | null => {
  try {
    const nameEQ = `${name}=`;
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      let cookie = cookies[i];
      while (cookie.charAt(0) === ' ') {
        cookie = cookie.substring(1);
      }
      if (cookie.indexOf(nameEQ) === 0) {
        return cookie.substring(nameEQ.length);
      }
    }
    return null;
  } catch (error) {
    console.error(`Error getting cookie ${name}:`, error);
    return null;
  }
};

/**
 * Delete a cookie by name
 */
export const deleteCookie = (name: string): void => {
  try {
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;SameSite=Strict`;
  } catch (error) {
    console.error(`Error deleting cookie ${name}:`, error);
  }
};

/**
 * Check if a cookie exists
 */
export const cookieExists = (name: string): boolean => {
  return getCookie(name) !== null;
};

// Age verification specific functions
const AGE_VERIFICATION_COOKIE = 'age-verified';

/**
 * Set the age verification status
 */
export const setAgeVerified = (verified = true): void => {
  setCookie(AGE_VERIFICATION_COOKIE, verified ? 'true' : 'false');
};

/**
 * Check if the user has verified their age
 */
export const isAgeVerified = (): boolean => {
  return getCookie(AGE_VERIFICATION_COOKIE) === 'true';
};

/**
 * Reset the age verification status
 */
export const resetAgeVerification = (): void => {
  deleteCookie(AGE_VERIFICATION_COOKIE);
};
